import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  ChoiceControl,
  DataTable,
  DetailList,
  Dialog,
  Field,
  Tabs,
} from "../../../ui";
import type { DataColumn } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import type { Command } from "../../workspace/model";
import { failure } from "../../workspace/behavior";
import { bankName, ceilWonAmount, date, documentTypeLabel, money } from "../../workspace/model";
import {
  CommandDialog,
  PageHeader,
  Panel,
  StateNotice,
  handleAdminLinkNavigation,
  statusBadge,
} from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import {
  parseApprovedTransactionAmount,
  TRANSACTION_REASON_MAX_LENGTH,
  TRANSACTION_SUPPLEMENT_REASON_MIN_LENGTH,
} from "../schema";
import type {
  TransactionAdminDetailViewV2,
} from "../flow";
import { TransactionDocumentGuide } from "./TransactionDocumentGuide";

export type TransactionDetailTab = "제출 증빙" | "심사 이력" | "결제·송금";
type DetailState = "ready" | "loading" | "error" | "not-found";
type TransactionDialog = "claim" | "approve" | "reject" | "supplement";

export interface TransactionDocumentPreview {
  readonly url: string;
  readonly contentType: string;
}

export interface TransactionDetailViewProps {
  readonly detail?: TransactionAdminDetailViewV2;
  readonly state?: DetailState;
  readonly actorId: number;
  readonly canReview: boolean;
  readonly allowAmountAdjustment?: boolean;
  readonly onBack: () => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly detailHref?: string;
  readonly onRetry: () => void;
  readonly onRefresh: () => Promise<TransactionAdminDetailViewV2 | undefined>;
  readonly onCommand: (command: Command) => Promise<unknown>;
  readonly onDownloadDocument: (
    document: Schemas["TransactionDocumentView"],
  ) => Promise<TransactionDocumentPreview>;
  readonly onReleasePreview: (url: string) => void;
  readonly memberHref?: string;
  readonly onOpenMember?: () => void;
  readonly recipientAccountAction?: React.ReactNode;
}

const reviewColumns: readonly DataColumn<Schemas["TransactionReviewView"]>[] = [
  { key: "round", title: "회차", numeric: true, render: (row) => row.reviewRound },
  { key: "admin", title: "담당 관리자 ID", render: (row) => row.assignedAdminLoginId ?? "—" },
  { key: "decision", title: "결정", render: (row) => row.decision ? statusBadge(row.decision) : "진행 중" },
  { key: "reason", title: "결정 사유", render: (row) => row.decisionReason ?? "—" },
  { key: "claimed", title: "선점일시", render: (row) => date(row.claimedAt) },
  { key: "expires", title: "잠금 만료", render: (row) => date(row.lockExpiresAt) },
  { key: "decided", title: "결정일시", render: (row) => date(row.decidedAt) },
];

const attemptColumns: readonly DataColumn<Schemas["ProviderAttemptView"]>[] = [
  { key: "attempt", title: "시도", numeric: true, render: (row) => row.attemptNo },
  { key: "operation", title: "작업", render: (row) => row.operationType.replaceAll("_", " ") },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "result", title: "결과", render: (row) => row.resultCode ? `${row.resultCode} · ${row.resultMessage ?? "설명 없음"}` : "—" },
  { key: "requested", title: "요청일시", render: (row) => date(row.requestedAt) },
  { key: "responded", title: "응답일시", render: (row) => date(row.respondedAt) },
];

function claimBlockMessage(
  availability: Schemas["TransactionReviewAvailability"],
  assignedAdminLoginId: string | null | undefined,
): string | null {
  if (availability.claimBlockReason === "NO_PERMISSION") return "거래 심사 권한이 없어 조회만 할 수 있습니다.";
  if (availability.claimBlockReason === "ASSIGNED_TO_OTHER") {
    return assignedAdminLoginId
      ? `다른 담당자(ID ${assignedAdminLoginId})가 심사 중입니다. 잠금이 유효한 동안 처리할 수 없습니다.`
      : "다른 담당자가 심사 중입니다. 잠금이 유효한 동안 처리할 수 없습니다.";
  }
  if (availability.claimBlockReason === "STATE_NOT_REVIEWABLE") return "이미 결정되었거나 현재 심사할 수 없는 거래입니다.";
  if (availability.claimable && !availability.decidable) return "기존 선점이 만료되었습니다. 거래를 다시 선점한 뒤 심사해 주세요.";
  return null;
}

export function TransactionDetailView({
  detail,
  state = "ready",
  actorId,
  canReview,
  allowAmountAdjustment = false,
  onBack,
  backHref,
  backLabel,
  detailHref,
  onRetry,
  onRefresh,
  onCommand,
  onDownloadDocument,
  onReleasePreview,
  memberHref,
  onOpenMember,
  recipientAccountAction,
}: TransactionDetailViewProps) {
  const [tab, setTab] = useState<TransactionDetailTab>("제출 증빙");
  const [dialog, setDialog] = useState<TransactionDialog | null>(null);
  const [reviewedDocuments, setReviewedDocuments] = useState<number[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Schemas["TransactionDocumentView"]>();
  const [preview, setPreview] = useState<TransactionDocumentPreview>();
  const [fitDocumentToViewport, setFitDocumentToViewport] = useState(true);
  const [documentConfirmed, setDocumentConfirmed] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number>();
  const [amountMatched, setAmountMatched] = useState(false);
  const [accountHolderMatched, setAccountHolderMatched] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState("");
  const [paymentPartyType, setPaymentPartyType] = useState("INDIVIDUAL");
  const [paymentPartyNumber, setPaymentPartyNumber] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pageError, setPageError] = useState("");
  const previewRef = useRef<TransactionDocumentPreview | undefined>(undefined);

  const releasePreview = () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
  };

  const closeDocument = () => {
    releasePreview();
    setSelectedDocument(undefined);
    setDocumentConfirmed(false);
    setFitDocumentToViewport(true);
  };

  const resetChecks = () => {
    closeDocument();
    setReviewedDocuments([]);
    setAmountMatched(false);
    setAccountHolderMatched(false);
    setPageError("");
  };

  useEffect(() => {
    setApprovedAmount(detail ? String(detail.transaction.requestedAmount) : "");
    setPaymentPartyType("INDIVIDUAL");
    setPaymentPartyNumber("");
    setTab("제출 증빙");
    setDialog(null);
    setReviewedDocuments([]);
    setAmountMatched(false);
    setAccountHolderMatched(false);
    setUncertain(false);
    setRefreshing(false);
    setPageError("");
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
    setSelectedDocument(undefined);
  }, [detail?.transaction.id, onReleasePreview]);

  useEffect(() => () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
  }, [onReleasePreview]);

  if (!detail || state !== "ready") {
    return (
      <>
        <PageHeader
          id="ADM-S08"
          hideBody
          title="거래 상세"
          description=""
          breadcrumbs={[{ label: "거래 심사", href: "/transactions/reviews" }, { label: "거래 상세", href: detailHref ?? "#" }]}
          backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
          action={!backHref ? <Button size="small" variant="quiet" onClick={onBack}>거래 목록으로</Button> : undefined}
        />
        {state === "not-found" ? <StateNotice state="not-found" /> : null}
        {state === "loading" ? <p role="status">거래 정보를 불러오는 중입니다.</p> : null}
        {state === "error" ? (
          <Alert persistent variant="danger" title="거래 정보를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>
            잠시 후 다시 조회해 주세요.
          </Alert>
        ) : null}
      </>
    );
  }

  const transaction = detail.transaction;
  const transferUnselected = transaction.status === "TRANSFER_SELECTION_PENDING";
  const latestReview = detail.reviews.reduce<Schemas["TransactionReviewView"] | undefined>(
    (latest, review) => !latest || review.reviewRound > latest.reviewRound ? review : latest,
    undefined,
  );
  const claimedBySelf = latestReview?.assignedAdminId === actorId && latestReview.decision == null;
  const reviewableStatus = ["UNDER_REVIEW", "REVIEWING"].includes(transaction.status);
  const canClaim = canReview && detail.reviewAvailability.claimable && reviewableStatus && !uncertain;
  const canDecide = canReview && detail.reviewAvailability.decidable && reviewableStatus && !uncertain;
  const allDocumentsReviewed = detail.documents.length > 0
    && detail.documents.every((document) => reviewedDocuments.includes(document.id));
  const parsedApprovedAmount = allowAmountAdjustment
    ? parseApprovedTransactionAmount(approvedAmount, transaction.requestedAmount)
    : ceilWonAmount(transaction.requestedAmount);
  const canApprove = canDecide
    && allDocumentsReviewed
    && amountMatched
    && accountHolderMatched
    && parsedApprovedAmount !== null
    && paymentPartyNumber.length === (paymentPartyType === "INDIVIDUAL" ? 13 : 10);
  const blockMessage = claimBlockMessage(detail.reviewAvailability, latestReview?.assignedAdminLoginId);
  const approvalSteps = [
    !allDocumentsReviewed ? detail.documents.length === 0
      ? "제출 증빙이 없습니다. 회원에게 증빙을 제출받은 뒤 최신 상태를 조회해 주세요."
      : `제출 증빙 ${detail.documents.length}건을 각각 열고 ‘서류 내용을 직접 확인했습니다’를 선택한 뒤 확인을 완료해 주세요.` : null,
    !amountMatched ? "증빙 금액과 요청 금액을 대조한 뒤 금액 일치 항목을 선택해 주세요." : null,
    !accountHolderMatched ? "증빙의 예금주와 수취 계좌 정보를 대조한 뒤 예금주 일치 항목을 선택해 주세요." : null,
    parsedApprovedAmount === null ? "승인 금액을 신청 금액 이하의 양수로 입력해 주세요." : null,
    paymentPartyNumber.length !== (paymentPartyType === "INDIVIDUAL" ? 13 : 10) ? "납부 대상의 개인/법인 구분과 식별번호를 입력해 주세요." : null,
  ].filter((step): step is string => step !== null);
  const approvalBlockReason = !canDecide
    ? uncertain ? "직전 처리 결과가 확정되지 않았습니다. 최신 상태를 조회한 뒤 다시 시도해 주세요." : blockMessage ?? "먼저 거래를 선점해 심사 권한과 현재 상태를 확인해 주세요."
    : "아래 확인 항목을 완료한 뒤 승인할 수 있습니다.";

  const openDocument = async (document: Schemas["TransactionDocumentView"]) => {
    if (downloadingDocumentId !== undefined) return;
    setDownloadingDocumentId(document.id);
    setPageError("");
    try {
      releasePreview();
      const result = await onDownloadDocument(document);
      previewRef.current = result;
      setPreview(result);
      setSelectedDocument(document);
      setDocumentConfirmed(reviewedDocuments.includes(document.id));
      setFitDocumentToViewport(true);
    } catch (cause) {
      setPageError(failure(cause).message);
    } finally {
      setDownloadingDocumentId(undefined);
    }
  };

  const refreshLatest = async () => {
    const result = await onRefresh();
    resetChecks();
    setUncertain(false);
    return result;
  };

  const refreshFromPage = () => {
    if (refreshing) return;
    setRefreshing(true);
    setPageError("");
    void refreshLatest()
      .catch((cause) => setPageError(failure(cause).message))
      .finally(() => setRefreshing(false));
  };

  const execute = async (command: Command) => {
    setUncertain(true);
    try {
      return await onCommand(command);
    } catch (cause) {
      if (!failure(cause).reconcile) setUncertain(false);
      throw cause;
    }
  };

  const documentColumns = [
    { key: "type", title: "서류", render: (row: Schemas["TransactionDocumentView"]) => documentTypeLabel(row.documentTypeCode, row.documentTypeName) },
    { key: "file", title: "파일 ID", numeric: true, render: (row: Schemas["TransactionDocumentView"]) => row.fileAssetId },
    { key: "status", title: "검토", render: (row: Schemas["TransactionDocumentView"]) => statusBadge(reviewedDocuments.includes(row.id) ? "COMPLETED" : row.reviewStatus) },
    {
      key: "action",
      title: "작업",
      render: (row: Schemas["TransactionDocumentView"]) => (
        <Button
          disabled={downloadingDocumentId !== undefined}
          isLoading={downloadingDocumentId === row.id}
          size="small"
          variant="quiet"
          onClick={() => { void openDocument(row); }}
        >
          {reviewedDocuments.includes(row.id) ? "다시 보기" : "파일 열기"}
        </Button>
      ),
    },
  ] satisfies readonly DataColumn<Schemas["TransactionDocumentView"]>[];

  const tabs = [
    {
      id: "제출 증빙",
      label: "제출 증빙",
      content: (
        <Panel title="제출 증빙" description="각 파일을 열어 내용과 금액을 확인해 주세요.">
          <TransactionDocumentGuide guide={transaction.documentGuide} />
          {transaction.latestSupplement?.reason ? (
            <Alert persistent variant="information" title="최근 보완 요청 사유">
              {transaction.latestSupplement.reason}
            </Alert>
          ) : null}
          <DataTable caption="제출 서류" columns={documentColumns} rows={detail.documents} rowKey={(row) => row.id} />
        </Panel>
      ),
    },
    {
      id: "심사 이력",
      label: "심사 이력",
      content: (
        <Panel title="심사 이력" description="서버에 기록된 담당자, 잠금과 결정 이력입니다.">
          {detail.reviews.length > 0
            ? <DataTable caption="거래 심사 이력" columns={reviewColumns} rows={detail.reviews} rowKey={(row) => row.id} />
            : <p>기록된 심사 이력이 없습니다.</p>}
        </Panel>
      ),
    },
    {
      id: "결제·송금",
      label: "결제·송금",
      content: (
        <div className="admin-split">
          <Panel title="결제 상태·시도 이력">
            <DetailList items={[
              { label: "카드 결제 상태", value: transaction.paymentStatus === "APPROVED" ? "결제 완료" : transaction.paymentStatus ? statusBadge(transaction.paymentStatus) : "결제 전" },
              { label: "결제 승인일시", value: date(transaction.paymentApprovedAt) },
            ]} />
            {detail.paymentAttempts.length > 0
              ? <DataTable caption="결제 시도 이력" columns={attemptColumns} rows={detail.paymentAttempts} rowKey={(row) => row.id} />
              : <p>결제 시도 이력이 없습니다.</p>}
          </Panel>
          <Panel title="송금 상태·시도 이력">
            <DetailList items={[
              { label: "송금 상태", value: transferUnselected ? "송금 미선택" : transaction.transferStatus ? statusBadge(transaction.transferStatus) : "송금 요청 없음" },
              { label: "송금 방식", value: transferUnselected ? "미선택" : transaction.transferId ? transaction.transferScheduleType === "SCHEDULED" ? "지정일 송금" : transaction.transferScheduleType === "IMMEDIATE" ? "즉시 송금" : "확인 필요" : "—" },
              ...(transaction.transferId && transaction.transferScheduleType === "SCHEDULED" ? [{ label: "송금 예정일시", value: date(transaction.transferScheduledAt ?? transaction.requestedTransferAt) }] : []),
              ...(transaction.transferCompletedAt ? [{ label: "송금 완료일시", value: date(transaction.transferCompletedAt) }] : []),
            ]} />
            {detail.transferAttempts.length > 0
              ? <DataTable caption="송금 시도 이력" columns={attemptColumns} rows={detail.transferAttempts} rowKey={(row) => row.id} />
              : <p>송금 시도 이력이 없습니다.</p>}
          </Panel>
        </div>
      ),
    },
  ] satisfies Array<{ id: TransactionDetailTab; label: string; content: React.ReactNode }>;

  return (
    <>
      <PageHeader
        id="ADM-S08"
          hideBody
        title="거래 상세"
        description=""
        breadcrumbs={[{ label: "거래 심사", href: "/transactions/reviews" }, { label: "거래 상세", href: detailHref ?? "#" }]}
        backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
        meta={(
          <p className="admin-transaction-meta" data-numeric>
            <span>{transaction.transactionNo}</span>
            {statusBadge(transaction.status)}
          </p>
        )}
        action={(
          <div className="admin-inline-actions">
            <Button isLoading={refreshing} size="small" variant="quiet" onClick={refreshFromPage}>최신 상태 조회</Button>
            {!backHref ? <Button size="small" variant="quiet" onClick={onBack}>거래 목록으로</Button> : null}
          </div>
        )}
      />
      {uncertain ? <StateNotice state="conflict" onRefresh={refreshFromPage} /> : null}
      {pageError ? <Alert persistent variant="danger" title="요청을 처리하지 못했습니다">{pageError}</Alert> : null}
      {transferUnselected && transaction.paymentStatus === "APPROVED" ? <Alert persistent variant="information" title="회원의 송금 선택을 기다리고 있습니다">결제는 완료되었고 아직 송금은 요청되지 않았습니다. 회원이 즉시 또는 지정일 송금을 요청할 때까지 결제 승인 상태를 유지합니다.</Alert> : null}
      {blockMessage ? <Alert persistent variant="warning" title="심사 처리 제한">{blockMessage}</Alert> : null}
      <Tabs items={tabs} value={tab} onChange={(value) => setTab(value as TransactionDetailTab)} />
      <div className="admin-split">
        <Panel title="거래 요약">
          <DetailList items={[
            { label: "거래 종류", value: transaction.categoryName },
            { label: "거래명", value: transaction.title },
            { label: "거래 금액", value: money(transaction.approvedAmount ?? transaction.requestedAmount) },
            { label: "송금 은행명", value: detail.recipientBankName ?? bankName(transaction.recipientBankCode) },
            { label: "송금 계좌번호", value: transaction.recipientAccountNumberMasked },
            { label: "예금주명", value: transaction.recipientHolderMasked ?? "정보 없음" },
            { label: "회원명(상태)", value: <>{memberHref ? <a className="admin-row-link" href={memberHref} onClick={onOpenMember ? (event) => handleAdminLinkNavigation(event, onOpenMember) : undefined}>{detail.memberSummary?.name ?? "정보 없음"}</a> : detail.memberSummary?.name ?? "정보 없음"} {detail.memberSummary?.status ? statusBadge(detail.memberSummary.status) : null}</> },
            { label: "당월 잔여 한도", value: detail.memberSummary?.limitUsage ? money(detail.memberSummary.limitUsage.availableAmount) : "정보 없음" },
          ]} />
          {recipientAccountAction}
        </Panel>
        <Panel
          title="심사 처리"
          description={canApprove ? "승인 전제조건을 모두 확인했습니다." : "서류, 금액과 예금주를 각각 대조해 주세요."}
        >
          <div className="admin-review-checks" aria-label="승인 대조 항목">
            <ChoiceControl
              checked={amountMatched}
              disabled={!canDecide}
              label={`요청 금액 ${money(transaction.requestedAmount)}이 증빙과 일치합니다`}
              onChange={(event) => setAmountMatched(event.currentTarget.checked)}
            />
            <ChoiceControl
              checked={accountHolderMatched}
              disabled={!canDecide}
              label={`예금주 ${transaction.recipientHolderMasked ?? "정보 없음"}이 증빙과 일치합니다`}
              onChange={(event) => setAccountHolderMatched(event.currentTarget.checked)}
            />
          </div>
          {allowAmountAdjustment ? (
            <Field
              compact
              description={parsedApprovedAmount !== null ? `원 미만은 올림합니다. 제출 금액 ${money(parsedApprovedAmount)}` : "원 미만 금액은 올림하여 제출합니다."}
              error={approvedAmount && parsedApprovedAmount === null ? "신청금액 이하의 양수를 입력해 주세요." : undefined}
              label="승인 금액"
              max={transaction.requestedAmount}
              min={0.01}
              step="0.01"
              type="number"
              value={approvedAmount}
              onChange={(event) => setApprovedAmount(event.currentTarget.value)}
            />
          ) : null}
          {detail.paymentParty ? <DetailList items={[
            { label: "납부 대상", value: detail.paymentParty.type === "BUSINESS" ? "법인" : "개인" },
            { label: "식별번호", value: detail.paymentParty.numberMasked },
            { label: "관리번호", value: detail.paymentParty.managementNo },
          ]}/> : null}
          {reviewableStatus ? <div className="admin-form-grid">
            <Field compact control="select" label="납부 대상" required disabled={!canDecide} value={paymentPartyType}
              onChange={(event) => { setPaymentPartyType(event.currentTarget.value); setPaymentPartyNumber(""); }}>
              <option value="INDIVIDUAL">개인</option><option value="BUSINESS">법인</option>
            </Field>
            <Field compact label={paymentPartyType === "INDIVIDUAL" ? "주민등록번호" : "사업자등록번호"} required
              type="password" inputMode="numeric" autoComplete="off" disabled={!canDecide}
              maxLength={paymentPartyType === "INDIVIDUAL" ? 13 : 10} value={paymentPartyNumber}
              description={paymentPartyType === "INDIVIDUAL" ? "납부받는 개인의 13자리 번호를 입력해 주세요." : "납부받는 법인의 10자리 번호를 입력해 주세요."}
              onChange={(event) => setPaymentPartyNumber(event.currentTarget.value.replace(/\D/g, "").slice(0, paymentPartyType === "INDIVIDUAL" ? 13 : 10))}/>
          </div> : null}
          <div className="admin-detail-actions">
            {canClaim ? (
              <Button size="small" variant={canDecide ? "secondary" : "primary"} onClick={() => setDialog("claim")}>
                {claimedBySelf ? "심사 잠금 연장" : latestReview?.assignedAdminId && detail.reviewAvailability.claimable ? "심사 다시 선점" : "내가 심사하기"}
              </Button>
            ) : null}
            {canDecide ? <Button size="small" variant="danger" onClick={() => setDialog("reject")}>반려</Button> : null}
            {canDecide ? <Button size="small" variant="secondary" onClick={() => setDialog("supplement")}>자료 보완 요청</Button> : null}
            {canReview ? <GuidedActionButton size="small" blockedReason={canApprove ? null : approvalBlockReason} guideItems={canDecide && !canApprove ? approvalSteps : undefined} onClick={() => setDialog("approve")}>승인</GuidedActionButton> : null}
          </div>
        </Panel>
      </div>
      <Dialog
        open={Boolean(selectedDocument && preview)}
        onClose={closeDocument}
        size="large"
        className="admin-document-dialog"
        title="거래 증빙 미리보기"
        description={selectedDocument
          ? `${documentTypeLabel(selectedDocument.documentTypeCode, selectedDocument.documentTypeName)} · 서버에서 받은 증빙 파일을 거래 정보와 대조합니다.`
          : "서버에서 받은 증빙 파일을 거래 정보와 대조합니다."}
        primaryAction={(
          <div className="admin-document-confirmation">
            <ChoiceControl
              checked={documentConfirmed}
              label="서류 내용을 직접 확인했습니다"
              onChange={(event) => setDocumentConfirmed(event.currentTarget.checked)}
            />
            <div className="admin-document-confirmation__actions">
              <Button size="small" variant="quiet" onClick={closeDocument}>닫기</Button>
              <GuidedActionButton
                size="small"
                blockedReason={documentConfirmed ? null : "서류 내용을 직접 확인한 뒤 ‘서류 내용을 직접 확인했습니다’를 선택해 주세요."}
                onClick={() => {
                  if (selectedDocument) {
                    setReviewedDocuments((current) => current.includes(selectedDocument.id)
                      ? current
                      : [...current, selectedDocument.id]);
                  }
                  closeDocument();
                }}
              >
                서류 확인 완료
              </GuidedActionButton>
            </div>
          </div>
        )}
      >
        {selectedDocument && preview ? (
          <div className="admin-command">
            {preview.contentType.startsWith("image/") ? (
              <div className="admin-document-preview">
                <div className="admin-document-preview__toolbar" aria-label="이미지 보기 설정">
                  <p>원본 이미지의 글자와 숫자를 확대해 확인할 수 있습니다.</p>
                  <div className="admin-document-preview__actions">
                    <a className="admin-row-link" href={preview.url} download={`거래증빙-${selectedDocument.fileAssetId}`}>원본 파일 저장</a>
                    <Button
                      size="small"
                      variant="quiet"
                      onClick={() => setFitDocumentToViewport((current) => !current)}
                    >
                      {fitDocumentToViewport ? "원본 크기" : "화면 맞춤"}
                    </Button>
                  </div>
                </div>
                <div className="admin-document-preview__viewport" data-fit={fitDocumentToViewport ? "viewport" : "original"}>
                  <img src={preview.url} alt="거래 증빙 파일" className="admin-document-preview__image" />
                </div>
              </div>
            ) : (
              <div className="admin-document-preview">
                <div className="admin-document-preview__toolbar">
                  <p>문서가 흐리게 보이면 원본 파일로 확인해 주세요.</p>
                  <a className="admin-row-link" href={preview.url} download={`거래증빙-${selectedDocument.fileAssetId}`}>원본 파일 저장</a>
                </div>
                <object className="admin-document-preview__object" data={preview.url} type={preview.contentType} aria-label="거래 증빙 파일">
                  <a href={preview.url} download={`거래증빙-${selectedDocument.fileAssetId}`}>증빙 파일 저장</a>
                </object>
              </div>
            )}
          </div>
        ) : null}
      </Dialog>
      <CommandDialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        title={dialog === "claim" ? "이 거래를 선점할까요?" : dialog === "supplement" ? "자료 보완을 요청할까요?" : "심사 결과를 저장할까요?"}
        description="현재 화면에서 확인한 최신 상태를 기준으로 요청합니다."
        operation={dialog === "claim" ? "거래 심사 선점" : dialog === "supplement" ? "자료 보완 요청" : "거래 심사 결정"}
        target={transaction.transactionNo}
        amount={money(dialog === "approve" ? parsedApprovedAmount : transaction.requestedAmount)}
        confirmLabel={dialog === "approve" ? "승인 요청" : dialog === "reject" ? "반려 요청" : dialog === "supplement" ? "보완 요청" : "심사 선점"}
        danger={dialog === "reject"}
        reasonRequired={dialog !== "claim"}
        reasonLabel={dialog === "supplement" ? "보완 사유" : "심사 사유"}
        reasonMinLength={dialog === "supplement" ? TRANSACTION_SUPPLEMENT_REASON_MIN_LENGTH : 1}
        reasonMaxLength={TRANSACTION_REASON_MAX_LENGTH}
        confirmDisabled={dialog === "approve" && !canApprove}
        onRefresh={async () => { await refreshLatest(); }}
        onCommand={execute}
        onComplete={async () => { setPaymentPartyNumber(""); await refreshLatest(); }}
        buildCommand={(reason): Command => dialog === "claim"
          ? { operation: "POST /api/v1/admin/transactions/{transactionId}/reviews/claim", params: { transactionId: transaction.id }, body: undefined }
          : dialog === "supplement"
            ? { operation: "POST /api/v1/admin/transactions/{transactionId}/reviews/supplement-requests", params: { transactionId: transaction.id }, body: { reason } }
            : {
              operation: "POST /api/v1/admin/transactions/{transactionId}/reviews/decision",
              params: { transactionId: transaction.id },
              body: {
                approved: dialog === "approve",
                approvedAmount: dialog === "approve" ? parsedApprovedAmount : null,
                amountMatched,
                accountHolderMatched,
                documentComplete: allDocumentsReviewed,
                reason,
                ...(dialog === "approve" ? { paymentPartyType, paymentPartyNumber } : {}),
              },
            }}
      />
    </>
  );
}
